import Layout from "../../components/Layout";
import { fetchArticle, fetchArticles } from "../../contentful/article";
import { IArticle } from "../../@types/generated/contentful";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import Image from "next/image";
import { ArrowLeftCircleIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/router";
import { useGetVehiclesQuery } from "../../graphql/generated/graphql";
import { TIER_KEYS } from "../../utils/constants";
import Grid from "../../components/Grid";
import GoToWatchlistCard from "../../components/GoToWatchlistCard";
import SEO from "../../components/SEO";

//
function getImageRender(node) {
  return (
    <div className="mt-3 relative w-full aspect-w-4 aspect-h-3">
      <Image
        alt=""
        src={`https:${node.data.target.fields.file.url}`}
        // height={node.data.target.fields.file.details.image.height}
        // width={node.data.target.fields.file.details.image.width}
        className="rounded-md h-full w-full"
        layout="fill"
        objectFit="cover"
      />
    </div>
  );
}

function getHeaderRender(node) {
  return (
    <h1 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
      {node.content[0].value}
    </h1>
  );
}

function getSecondaryHeaderRender(node) {
  return (
    <h2 className="mt-6 text-2xl font-bold tracking-tight text-gray-900">
      {node.content[0].value}
    </h2>
  );
}

function getParagraphRender(node, children) {
  return (
    <p className="text-md text-gray-600 mt-6 landing-8 justify-center ">
      {children}
    </p>
  );
}

function getLinkRender({ data }, children) {
  return (
    <a
      href={data.uri}
      target="_blank"
      className="text-cyan-500 hover:text-cyan-400 inline-flex gap-x-1 items-center cursor-pointer font-medium"
    >
      {children}
    </a>
  );
}

// main render

export default function Article({ content, title, image, slug }) {
  const router = useRouter();
  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => getImageRender(node),
      [BLOCKS.HEADING_1]: (node) => getHeaderRender(node),
      [BLOCKS.HEADING_2]: (node) => getSecondaryHeaderRender(node),
      [BLOCKS.PARAGRAPH]: (node, children) =>
        getParagraphRender(node, children),
      [INLINES.HYPERLINK]: (node, children) => getLinkRender(node, children),
      [INLINES.ASSET_HYPERLINK]: (node, children) =>
        getLinkRender(node, children),
    },
  };

  const { data, loading: loadingLatestMaxVehicles } = useGetVehiclesQuery({
    variables: {
      limit: 24,
      filters: {
        listingTier: TIER_KEYS.MAX,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const { getVehicles } = data || {};
  const { data: vehiclesMax } = getVehicles || {};

  const { data: dataPlus, loading: loadingLatestPlusVehicles } =
    useGetVehiclesQuery({
      variables: {
        limit: 14,
        filters: {
          listingTier: TIER_KEYS.PLUS,
        },
      },
      fetchPolicy: "cache-and-network",
    });

  const { getVehicles: getVehiclesPlus } = dataPlus || {};
  const { data: vehiclesPlus } = getVehiclesPlus || {};

  return (
    <div className="flex flex-col col-span-1 max-w-4xl mx-auto px-4 py-12">
      <SEO
        currentURL={`${process.env.NEXT_PUBLIC_ROOT_URL}/blog/${slug}`}
        title={title}
        siteTitle="Movi Autos"
        previewImage={`https:${image}`}
      />
      <button
        onClick={() => router.back()}
        className={
          "mr-2 text-sm font-medium text-gray-900 hover:text-orange-500 flex items-center gap-x-2"
        }
      >
        <ArrowLeftCircleIcon className="h-7 w-7" />
        Regresar
      </button>
      <h1 className="w-auto mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
        {title}
      </h1>
      {documentToReactComponents(content, renderOptions)}
      <div className="mt-8">
        <Grid
          loading={loadingLatestMaxVehicles}
          items={vehiclesMax}
          title={
            <span className="">
              Últimos autos <span className="text-blue-500">MAX</span>
            </span>
          }
          LastElement={() => (
            <GoToWatchlistCard
              pathname="/autosusados"
              icon="list"
              label="Ver todos"
            />
          )}
          noPadding
        />

        <Grid
          loading={loadingLatestPlusVehicles}
          items={vehiclesPlus}
          title={
            <span className="">
              Últimos autos <span className="text-emerald-500">PLUS</span>
            </span>
          }
          LastElement={() => (
            <GoToWatchlistCard
              pathname="/autosusados"
              icon="list"
              label="Ver todos"
            />
          )}
          noPadding
        />
      </div>
    </div>
  );
}

// ---- NEXT functions
export async function getStaticPaths() {
  const res = await fetchArticles();
  const articles = await res.map((p: IArticle) => {
    return p.fields;
  });
  const allPaths = articles.map((path) => {
    return {
      params: {
        slug: path.slug.toString(),
      },
    };
  });

  return {
    paths: allPaths,
    fallback: false,
  };
}

export async function getStaticProps(ctx) {
  const id = ctx.params.slug;
  const articles: [IArticle] = await fetchArticle(id);
  const article = articles[0]
    ? {
        content: articles[0].fields.content,
        title: articles[0].fields.title,
        image: articles[0].fields.cover.fields.file.url,
      }
    : null;
  return {
    props: {
      content: article.content,
      title: article.title,
      image: article.image,
      slug: id,
    },
  };
}

Article.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};
